import { ArrowLeft, Compass } from 'lucide-react';
import { BotanicalAccent } from '@/components/ui/botanical-accent';
import { MagneticButton } from '@/components/ui/magnetic-button';
import { useLang } from '@/lib/LanguageContext';

type Lang = 'fr' | 'en';

const UI = {
  eyebrow: { fr: '404 — Page égarée', en: '404 — Lost page' },
  title: {
    fr: 'Cette page s\'est égarée comme une lettre dans la brume.',
    en: 'This page got lost like a letter in the fog.'
  },
  body: {
    fr: 'Vous êtes au bon endroit pour ce qui compte. Reprenons depuis le début, ou explorons la méthode.',
    en: 'You\'re in the right place for what matters. Let\'s start over, or explore the method.'
  },
  cta_home: { fr: 'Retour à l\'accueil', en: 'Back to home' },
  cta_methode: { fr: 'Voir la Méthode 5 Questions', en: 'See the 5-Question Method' }
};

function getText<T extends { fr: string; en: string }>(obj: T, lang: Lang): string {
  return obj[lang];
}

export function NotFoundPage() {
  const { lang } = useLang();

  return (
    <main className="surface-cream atmosphere-paper-cream relative flex min-h-[100dvh] w-full items-center justify-center overflow-hidden py-20 md:py-32">
      {/* Phase 27.18 — combo M.1 morning light rose-blush gradient at top */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-20 md:h-24 bg-gradient-to-b from-rose-blush/40 to-transparent"
      />
      {/* Phase 27.18 — combo M.3 rose-blush radial backdrop behind the 404 numeral (bottom-left) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 z-0 h-64 w-64"
        style={{
          background: 'radial-gradient(circle at center, oklch(0.94 0.025 5 / 0.3) 0%, transparent 70%)'
        }}
      />
      {/* Phase 27.10 — amplified : atmosphere paper grain + numeral 404 in margin */}
      <BotanicalAccent variant="bouquet-mixed" position="top-right" size="lg" opacity={0.14} />
      <BotanicalAccent variant="leaf-sage" position="bottom-left" size="md" opacity={0.10} />
      {/* Phase 27.18 — combo M.2 additional rose-tendril BotanicalAccent */}
      <BotanicalAccent variant="rose-tendril" position="bottom-right" size="md" opacity={0.15} />

      <div className="relative z-[1] mx-auto max-w-2xl px-5 text-center md:px-8">
        <span className="eyebrow-pill mb-6">
          {getText(UI.eyebrow, lang)}
        </span>

        <h1 className="mb-8 font-display text-[clamp(2.25rem,5vw,3.75rem)] leading-[1.1] text-balance text-foreground">
          {getText(UI.title, lang)}
        </h1>

        <div className="mx-auto my-4 h-px w-24 bg-secondary/40" aria-hidden="true" />

        <p className="mx-auto mt-4 mb-10 max-w-xl font-sans text-lg leading-relaxed text-pretty hyphens-auto text-muted-foreground">
          {getText(UI.body, lang)}
        </p>

        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <MagneticButton>
            <a
              href="/"
              data-cursor="cta"
              className="border-beam inline-flex items-center gap-3 rounded-md bg-primary px-8 py-4 font-sans text-sm font-bold uppercase tracking-widest text-primary-foreground transition-all hover:bg-primary/90"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
              {getText(UI.cta_home, lang)}
            </a>
          </MagneticButton>

          <a
            href="/#methode"
            className="inline-flex items-center gap-2 self-center border-b border-primary pb-1 font-sans text-base font-medium text-foreground transition-colors hover:text-primary"
          >
            <Compass className="h-4 w-4" strokeWidth={1.7} aria-hidden="true" />
            {getText(UI.cta_methode, lang)}
          </a>
        </div>
      </div>
    </main>
  );
}
